import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useRevalidator } from 'react-router-dom';
import { Button, Input, Label, Modal, cn } from '@immo/ui-kit';
import {
  IssueAdminKeyBodySchema,
  type AdminWorkspaceListItem,
  type IssueAdminKeyResponse,
  type AdminKeyScope,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { CopyIcon, SearchIcon } from '@/components/layout/icons';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';

// Deliberately NOT a defaulted value. A scope is the one field an operator must
// state, so `null` is the honest starting point and submit stays disabled until
// they choose — a preselected "all workspaces" would be a platform-wide grant
// nobody typed (parent BR 4).
type ScopeChoice = 'ALL' | 'SELECTED' | null;

type Field = 'name' | 'description' | 'rateLimit' | 'scope';

type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = { name: null, description: null, rateLimit: null, scope: null };

const DEFAULT_RATE_LIMIT = '30';

/**
 * Where each exit lands (IMM-622).
 *
 * This dialog is a **route** — `/admins/api-keys/new`, a child of `/admins` —
 * so closing it is a navigation and not a local `open` flag. That is why the
 * fix is two destinations rather than "cancel does not navigate": with no
 * navigation at all the modal, which is rendered `open` unconditionally, would
 * never close.
 *
 * Cancelling created nothing, so it returns the operator to the page they
 * opened the dialog from. Only an actually-issued key justifies moving them to
 * the key list.
 */
const CANCELLED_DESTINATION = '/admins';
const ISSUED_DESTINATION = '/workspaces/api-keys';

export function IssueAdminKeyPage() {
  const t = useTranslations('pages.admin.api-keys.issue-service');
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();

  const [workspaces, setWorkspaces] = useState<ReadonlyArray<AdminWorkspaceListItem>>([]);
  const [workspaceQuery, setWorkspaceQuery] = useState('');
  const [scopeChoice, setScopeChoice] = useState<ScopeChoice>(null);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<ReadonlyArray<string>>([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rateLimit, setRateLimit] = useState(DEFAULT_RATE_LIMIT);

  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState<IssueAdminKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;

    // Every workspace, test tenants included: issuing a key is a write, and
    // writes are never gated by the operator's test-workspace filter.
    void apiClient.adminWorkspaces.list({ query: ALL_WORKSPACES_QUERY }).then((result) => {
      if (!active) return;

      if (result.ok) setWorkspaces(result.body.data);
    });

    return () => {
      active = false;
    };
  }, []);

  const filteredWorkspaces = useMemo(() => {
    const query = workspaceQuery.trim().toLowerCase();

    if (query === '') return workspaces;

    return workspaces.filter((workspace) => workspace.name.toLowerCase().includes(query));
  }, [workspaces, workspaceQuery]);

  const clearError = (key: Field) => setErrors((prev) => (prev[key] === null ? prev : { ...prev, [key]: null }));

  const toggleWorkspace = (workspaceId: string) => {
    clearError('scope');
    setSelectedWorkspaceIds((prev) =>
      prev.includes(workspaceId) ? prev.filter((entry) => entry !== workspaceId) : [...prev, workspaceId],
    );
  };

  const chooseScope = (choice: ScopeChoice) => {
    clearError('scope');
    setScopeChoice(choice);
  };

  const close = () => {
    if (submitting) return;

    // The `issued !== null` split already existed here — it just governed the
    // revalidation alone while the navigation ran either way, sending an
    // operator who cancelled to a different section of the app.
    if (issued !== null) {
      revalidator.revalidate();
      navigate(ISSUED_DESTINATION);

      return;
    }

    navigate({ pathname: CANCELLED_DESTINATION, search: location.search });
  };

  const scopeFor = (): AdminKeyScope | null => {
    if (scopeChoice === 'ALL') return { kind: 'ALL' };

    if (scopeChoice === 'SELECTED' && selectedWorkspaceIds.length > 0) {
      return { kind: 'SELECTED', workspaceIds: [...selectedWorkspaceIds] };
    }

    return null;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const scope = scopeFor();

    if (scope === null) {
      setErrors((prev) => ({
        ...prev,
        scope: t(scopeChoice === null ? 'errors.scope-required' : 'errors.scope-empty'),
      }));

      return;
    }

    const trimmedRate = rateLimit.trim();
    const parsed = IssueAdminKeyBodySchema.safeParse({
      name,
      ...(description.trim().length > 0 ? { description } : {}),
      ...(trimmedRate.length > 0 ? { rateLimit: Number(trimmedRate) } : {}),
      scope,
    });

    if (!parsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      for (const issue of parsed.error.issues) {
        const head = issue.path[0];

        if (head === 'name' && next.name === null) {
          next.name = t(issue.code === 'too_big' ? 'errors.name-too-long' : 'errors.name-required');
        } else if (head === 'description' && next.description === null) {
          next.description = t('errors.description-too-long');
        } else if (head === 'rateLimit' && next.rateLimit === null) {
          next.rateLimit = t('errors.rate-limit-range');
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminApiKeys.issueService({ body: parsed.data });

      if (result.ok) {
        setIssued(result.body);

        return;
      }

      // The form's values are deliberately preserved: a name clash is fixed by
      // editing one field, and clearing the dialog would make the operator
      // re-pick the whole scope.
      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

      if (code === 'identity.api-key.name-taken') {
        setErrors((prev) => ({ ...prev, name: t('errors.name-taken', { name: name.trim() }) }));
      } else if (code === 'identity.api-key.workspace-unknown') {
        setErrors((prev) => ({ ...prev, scope: t('errors.workspace-unknown') }));
      } else if (result.status === 403) {
        setGenericError(t('errors.forbidden'));
      } else {
        setGenericError(t('errors.generic'));
      }
    } catch {
      setGenericError(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = async () => {
    if (issued === null) return;

    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the secret stays visible for manual copy.
    }
  };

  if (issued !== null) {
    return (
      <Modal
        open
        // No `onClose` escape hatch beyond the acknowledge button, and no
        // `dirty` prompt: the secret exists in this component's state and
        // nowhere else, so a backdrop dismissal would destroy the only copy.
        onClose={close}
        size="md"
        title={t('secret-heading')}
        footer={
          <Button type="button" onClick={close}>
            {t('secret-acknowledge-cta')}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-base-sm text-text-secondary">{t('secret-warning')}</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={issued.secret} className="font-mono" aria-label={t('secret-heading')} />
            <Button type="button" variant="ghost" onClick={copySecret}>
              <CopyIcon className="h-4 w-4" />
              {copied ? t('copied-cta') : t('copy-cta')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const submitDisabled = submitting || scopeFor() === null;

  return (
    <Modal
      open
      onClose={close}
      dirty={name !== '' || description !== '' || scopeChoice !== null}
      size="lg"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('cancel-cta')}
          </Button>
          <Button type="submit" form="issue-admin-key-form" disabled={submitDisabled}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="issue-admin-key-form" className="space-y-5" onSubmit={submit} noValidate>
        <div className="space-y-1">
          <Label htmlFor="issue-service-name">{t('name-label')}</Label>
          <Input
            id="issue-service-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearError('name');
            }}
            disabled={submitting}
            invalid={errors.name !== null}
            aria-describedby={errors.name !== null ? 'issue-service-name-error' : undefined}
          />
          <p className="text-sm text-text-tertiary">{t('name-helper')}</p>
          {errors.name !== null && (
            <p id="issue-service-name-error" className="text-sm text-danger-600">
              {errors.name}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="issue-service-description">{t('description-label')}</Label>
          <Input
            id="issue-service-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearError('description');
            }}
            disabled={submitting}
            invalid={errors.description !== null}
          />
          {errors.description !== null && <p className="text-sm text-danger-600">{errors.description}</p>}
        </div>

        <div className="space-y-1">
          <Label htmlFor="issue-service-rate-limit">{t('rate-limit-label')}</Label>
          <Input
            id="issue-service-rate-limit"
            type="number"
            inputMode="numeric"
            min={1}
            max={60}
            className="w-24"
            value={rateLimit}
            onChange={(e) => {
              setRateLimit(e.target.value);
              clearError('rateLimit');
            }}
            disabled={submitting}
            invalid={errors.rateLimit !== null}
          />
          <p className="text-sm text-text-tertiary">{t('rate-limit-helper')}</p>
          {errors.rateLimit !== null && <p className="text-sm text-danger-600">{errors.rateLimit}</p>}
        </div>

        <fieldset className="space-y-2">
          <legend className="text-base-sm font-medium text-text-primary">{t('scope-label')}</legend>
          <p className="text-sm text-text-tertiary">{t('scope-helper')}</p>
          <div className="flex gap-2">
            {(['ALL', 'SELECTED'] as const).map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => chooseScope(choice)}
                aria-pressed={scopeChoice === choice}
                disabled={submitting}
                className={cn(
                  'rounded-md border px-3 py-2 text-base-sm focus-visible:shadow-focus focus-visible:outline-none',
                  scopeChoice === choice
                    ? 'border-interactive bg-interactive-subtle-bg text-text-primary'
                    : 'border-border text-text-secondary hover:bg-subtle',
                )}
              >
                {t(choice === 'ALL' ? 'scope-all' : 'scope-selected')}
              </button>
            ))}
          </div>

          {scopeChoice === 'SELECTED' && (
            <div className="space-y-1">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
                <Input
                  className="pl-8"
                  value={workspaceQuery}
                  placeholder={t('workspace-search-placeholder')}
                  onChange={(e) => setWorkspaceQuery(e.target.value)}
                  disabled={submitting}
                  aria-label={t('workspace-search-placeholder')}
                />
              </div>
              <ul className="max-h-40 overflow-y-auto rounded-md border border-border" aria-label={t('scope-selected')}>
                {filteredWorkspaces.map((workspace) => (
                  <li key={workspace.id}>
                    <button
                      type="button"
                      onClick={() => toggleWorkspace(workspace.id)}
                      aria-pressed={selectedWorkspaceIds.includes(workspace.id)}
                      disabled={submitting}
                      className={cn(
                        'flex w-full items-center justify-between px-3 py-2 text-left text-base-sm text-text-primary focus-visible:shadow-focus focus-visible:outline-none',
                        selectedWorkspaceIds.includes(workspace.id) ? 'bg-interactive-subtle-bg' : 'hover:bg-subtle',
                      )}
                    >
                      <span>{workspace.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errors.scope !== null && <p className="text-sm text-danger-600">{errors.scope}</p>}
        </fieldset>

        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}
